import { useEffect, useRef, useState } from 'react'
import { IconBuildingStore, IconChevronDown, IconChevronLeft, IconChevronRight, IconChevronUp, IconFile, IconHome, IconLayoutRows, IconLoader2 } from '@tabler/icons-react'
import { inferTemplateSlots, taxonomyLabelKey } from '@webbuilder/schemas'
import { getPublicTemplateData } from '../../lib/api'
import { extraPagesOf, getSections, moveSection, moveSectionTo, pageSlice, sectionVariants, setPageIncluded, swapSectionVariant, visitedVariant, withPageSlice, type CreateForm } from '../../lib/preview/compose'
import type { ElementNode } from '../../lib/preview/types'
import { useI18n } from '../../lib/i18n'

/** Las direcciones de las páginas de familia del catálogo, con su familia. */
const CATALOG_PAGE_KINDS: Record<string, 'products' | 'digital' | 'bookings' | 'services' | 'blog'> = {
  '/blog': 'blog',
  '/products': 'products',
  '/digital': 'digital',
  '/bookings': 'bookings',
  '/services': 'services',
}

/**
 * Las secciones de la portada, como tarjetas: su miniatura, su nombre y su
 * número; se arrastran por el asa o se mueven con las flechas, se apagan con
 * el interruptor y se prueban otras variantes. La variante nueva recibe los
 * MISMOS textos que la IA escribió para la sección —se reparten en sus slots—,
 * así que cambiar no gasta IA: sólo se descarga el template.
 *
 * Las variantes van numeradas y en orden fijo, la original la primera: con
 * ‹ › se va y se vuelve, y el número sirve para acordarse de la que gustó.
 *
 * La tarjeta y la vista previa se siguen: la sección que se ve en la página
 * marca su tarjeta (`activeRootId`), y al cambiar de variante la página baja a
 * esa sección y la resalta (`onReveal`).
 *
 * Con varias páginas, unas píldoras arriba eligen cuál —la vista previa va a
 * la misma— y cada página que no es la portada lleva su interruptor «Incluir
 * esta página». Lo demás es igual en todas.
 */
export function WizardSectionsStep({ token, loadVariant, form, onChange, activeRootId = null, onReveal, page = 0, onPage }: {
  /** Para descargar otra variante. Sin él, la descarga `loadVariant` (la demo, que no tiene cuenta). */
  token?: string
  loadVariant?: (templateId: string, language: 'es' | 'en') => Promise<unknown[]>
  form: CreateForm
  onChange: (next: CreateForm) => void
  /** La sección que se está viendo en la vista previa. */
  activeRootId?: string | null
  /** Llevar la vista previa a una sección y resaltarla. */
  onReveal?: (rootId: string) => void
  /** La página cuyas secciones se listan (0 = la portada). */
  page?: number
  onPage?: (page: number) => void
}) {
  const { t } = useI18n()
  const [loading, setLoading] = useState<number | null>(null)
  const [failed, setFailed] = useState<number | null>(null)
  const [dragging, setDragging] = useState<number | null>(null)
  const [over, setOver] = useState<number | null>(null)
  const listRef = useRef<HTMLOListElement>(null)

  /* La tarjeta de lo que se ve en la página, a la vista en su lista: sólo se
     desplaza la columna del paso, nunca la ventana. */
  useEffect(() => {
    const list = listRef.current
    const card = activeRootId ? list?.querySelector<HTMLElement>(`[data-root-ids~="${CSS.escape(activeRootId)}"]`) : null
    const scroller = list?.closest<HTMLElement>('.cwa-wz-scroll')
    if (!card || !scroller || scroller.scrollHeight <= scroller.clientHeight) return
    const box = scroller.getBoundingClientRect()
    const at = card.getBoundingClientRect()
    const margin = 16
    if (at.top < box.top + margin) scroller.scrollBy({ top: at.top - box.top - margin, behavior: 'smooth' })
    else if (at.bottom > box.bottom - margin) scroller.scrollBy({ top: at.bottom - box.bottom + margin, behavior: 'smooth' })
  }, [activeRootId])

  const extraPages = extraPagesOf(form)
  const extra = extraPages.find((item) => item.index === page)
  const slice = pageSlice(form, extra ? page : 0)
  const at = extra ? page : 0
  const excluded = Boolean(extra && !extra.included)
  const homeName = typeof form.base.pages[0]?.name === 'string' && form.base.pages[0].name ? form.base.pages[0].name : t('pages.home')

  /* Las páginas, como píldoras: sólo si hay más de una. La que no se incluye
     sale tachada, pero se puede abrir para verla y volver a incluirla. */
  /* Cuántas secciones van encendidas en cada página: se lee de un vistazo qué
     tiene cada una sin abrirla. */
  const sectionsOn = (index: number) => {
    const page = pageSlice(form, index)
    const roots = page.sections.length ? page.sections.map((section) => section.rootIds) : page.baseElements.map((node) => [node.id])
    return roots.filter((ids) => !ids.every((id) => page.disabledSections.includes(id))).length
  }
  /* La página de una familia del catálogo («Tienda», «Talleres»): llega sin
     secciones y el editor la llena con su catálogo al abrir el proyecto. Ver
     `asCatalogFamilyPages` en el worker. */
  const catalogKindOf = (slug: string) => CATALOG_PAGE_KINDS[slug] ?? null
  const pageTabs = extraPages.length ? (
    <div className="cwa-page-tabs" role="tablist" aria-label={t('cwa.pages.aria')}>
      {[{ index: 0, name: homeName, included: true }, ...extraPages].map((item) => {
        const count = sectionsOn(item.index)
        return (
          <button
            key={item.index}
            type="button"
            role="tab"
            aria-selected={item.index === at}
            className={`cwa-page-tab${item.index === at ? ' is-active' : ''}${item.included ? '' : ' is-off'}`}
            title={item.included ? (item.name || homeName) : t('cwa.pageExcluded')}
            onClick={() => onPage?.(item.index)}
          >
            <span className="cwa-page-tab-icon" aria-hidden="true">{item.index === 0 ? <IconHome size={15} /> : 'slug' in item && catalogKindOf(item.slug) ? <IconBuildingStore size={15} /> : <IconFile size={15} />}</span>
            <span className="cwa-page-tab-copy">
              <strong>{item.name || homeName}</strong>
              <small>{!item.included ? t('cwa.pageExcluded') : 'slug' in item && catalogKindOf(item.slug) ? t('cwa.pageCatalog.tab') : t(count === 1 ? 'cwa.pageSections.one' : 'cwa.pageSections.many', { count })}</small>
            </span>
          </button>
        )
      })}
    </div>
  ) : null

  /* «Incluir esta página», en las que no son la portada. */
  const includeRow = extra ? (
    <div className={`cwa-page-include${excluded ? ' is-off' : ''}`}>
      <span className="cwa-page-include-copy">
        <strong>{t('cwa.pageInclude')}</strong>
        <small>{t(excluded ? 'cwa.pageExcludedHint' : 'cwa.pageIncludeHint')}</small>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={!excluded}
        aria-label={t('cwa.pageInclude')}
        className={`cwa-switch${excluded ? '' : ' on'}`}
        onClick={() => onChange(setPageIncluded(form, at, excluded))}
      >
        <span />
      </button>
    </div>
  ) : null

  /* La página de una familia: no tiene secciones que ordenar, se llena sola. */
  const catalogKind = extra ? catalogKindOf(extra.slug) : null
  if (catalogKind && !slice.sections.length && !slice.baseElements.length) {
    return (
      <>
        {pageTabs}
        {includeRow}
        <div className={`cwa-page-catalog${excluded ? ' is-excluded' : ''}`}>
          <span className="cwa-page-catalog-icon" aria-hidden="true"><IconBuildingStore size={18} /></span>
          <span className="cwa-page-catalog-copy">
            <strong>{t('cwa.pageCatalog.title')}</strong>
            <small>{t(`cwa.pageCatalog.body.${catalogKind}`)}</small>
          </span>
        </div>
      </>
    )
  }

  // Borradores sin datos de secciones (los de antes): la lista simple de siempre.
  if (!slice.sections.length) {
    return (
      <>
        {pageTabs}
        {includeRow}
        <div className={`cwa-section-cards${excluded ? ' is-excluded' : ''}`} aria-disabled={excluded || undefined}>
          {getSections(slice.baseElements).map((section) => (
            <div key={section.id} className="cwa-section-card">
              <span className="cwa-section-copy"><strong>{section.label}</strong></span>
              <Switch form={form} page={at} rootIds={[section.id]} onChange={onChange} />
            </div>
          ))}
        </div>
      </>
    )
  }

  /** Lleva la sección a la variante `at` de su lista (la 0 es la original). */
  async function showVariant(index: number, variantAt: number) {
    const section = slice.sections[index]
    const variant = section ? sectionVariants(section)[variantAt] : undefined
    if (!section || !variant || variant.id === section.templateId || loading !== null) return
    /* Sólo se enseña una vez, cuando la nueva ya está pintada: llevar la página
       a la de antes y luego a la nueva la hacía subir, bajar y marcar dos veces. */
    setFailed(null)
    try {
      let elements: ElementNode[] = []
      // Una ya vista vuelve tal como estaba, sin descargar nada.
      if (!visitedVariant(section, variant.id)) {
        setLoading(index)
        const raw = loadVariant
          ? await loadVariant(variant.id, form.language)
          : (await getPublicTemplateData(token ?? '', variant.id, form.language)).elements
        // Un template sin marcas, con los slots deducidos: si no, se quedaría con su texto de muestra.
        elements = inferTemplateSlots(raw as ElementNode[], section.category).elements
      }
      const next = swapSectionVariant(form, index, variant.id, elements, at)
      onChange(next)
      const root = pageSlice(next, at).sections[index]?.rootIds[0]
      if (root) onReveal?.(root)
    } catch {
      setFailed(index)
    } finally {
      setLoading(null)
    }
  }

  return (
    <>
      {pageTabs}
      {includeRow}
      <ol className={`cwa-section-cards${excluded ? ' is-excluded' : ''}`} ref={listRef} aria-disabled={excluded || undefined}>
        {slice.sections.map((section, index) => {
          const key = taxonomyLabelKey('category', section.category)
          const label = key === section.category ? section.category : t(key)
          const off = section.rootIds.every((id) => slice.disabledSections.includes(id))
          const variants = sectionVariants(section)
          const current = Math.max(0, variants.findIndex((variant) => variant.id === section.templateId))
          const total = variants.length
          const active = activeRootId !== null && section.rootIds.includes(activeRootId)
          const step = (delta: -1 | 1) => void showVariant(index, (current + delta + total) % total)
          return (
            <li
              key={section.rootIds.join('|')}
              data-root-ids={section.rootIds.join(' ')}
              className={`cwa-section-card${off ? ' is-off' : ''}${active && !off ? ' is-active' : ''}${dragging === index ? ' is-dragging' : ''}${over === index && dragging !== index ? ' is-over' : ''}`}
              draggable
              // Pulsar la tarjeta (no sus botones) lleva la página a su sección.
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('button') || off || !section.rootIds[0]) return
                onReveal?.(section.rootIds[0])
              }}
              onDragStart={(event) => { setDragging(index); event.dataTransfer.effectAllowed = 'move' }}
              onDragOver={(event) => { event.preventDefault(); setOver(index) }}
              onDragLeave={() => setOver((current) => (current === index ? null : current))}
              onDrop={(event) => {
                event.preventDefault()
                if (dragging !== null && dragging !== index) onChange(moveSectionTo(form, dragging, index, at))
                setDragging(null)
                setOver(null)
              }}
              onDragEnd={() => { setDragging(null); setOver(null) }}
            >
              {/* Subir y bajar a la vista, a la izquierda, como en la lista de
                  secciones del editor; arrastrar la tarjeta sigue valiendo. */}
              <span className="cwa-section-order">
                <button type="button" aria-label={t('cwa.moveUp')} title={t('cwa.moveUp')} disabled={excluded || index === 0} onClick={() => onChange(moveSection(form, index, -1, at))}>
                  <IconChevronUp size={13} stroke={2.2} />
                </button>
                <button type="button" aria-label={t('cwa.moveDown')} title={t('cwa.moveDown')} disabled={excluded || index === slice.sections.length - 1} onClick={() => onChange(moveSection(form, index, 1, at))}>
                  <IconChevronDown size={13} stroke={2.2} />
                </button>
              </span>
              <span className="cwa-section-thumb" aria-hidden="true">
                {section.thumbnailUrl ? <img src={section.thumbnailUrl} alt="" loading="lazy" /> : <IconLayoutRows size={18} />}
                <b className="cwa-section-number">{index + 1}</b>
              </span>
              <span className="cwa-section-copy">
                <strong>{label}</strong>
                {section.copy.headline ? <em>{section.copy.headline}</em> : null}
              </span>
              <span className="cwa-section-actions">
                {/* Otra variante, sin IA: ‹ 2/5 ›. Sólo si el catálogo tiene
                    otras; el número del medio devuelve a la original. */}
                {total > 1 ? (
                <span className="cwa-variant-pager" role="group" aria-label={t('cwa.variant')}>
                  <button type="button" className="cwa-variant-step" disabled={loading !== null || off} aria-label={t('cwa.variantPrev')} title={t('cwa.variantPrev')} onClick={() => step(-1)}>
                    <IconChevronLeft size={14} />
                  </button>
                  <button
                    type="button"
                    className="cwa-variant-count"
                    disabled={loading !== null || off || current === 0}
                    title={current === 0 ? t('cwa.variantOriginal') : t('cwa.variantBackOriginal')}
                    onClick={() => void showVariant(index, 0)}
                  >
                    {loading === index ? <IconLoader2 className="cwa-spin" size={13} /> : `${current + 1}/${total}`}
                  </button>
                  <button type="button" className="cwa-variant-step" disabled={loading !== null || off} aria-label={t('cwa.variantNext')} title={t('cwa.variant')} onClick={() => step(1)}>
                    <IconChevronRight size={14} />
                  </button>
                </span>
                ) : null}
                <Switch form={form} page={at} rootIds={section.rootIds} onChange={onChange} />
              </span>
              {failed === index ? <p className="cwa-hint cwa-hint--warn">{t('cwa.variantFailed')}</p> : null}
            </li>
          )
        })}
      </ol>
    </>
  )
}

function Switch({ form, page, rootIds, onChange }: { form: CreateForm; page: number; rootIds: string[]; onChange: (next: CreateForm) => void }) {
  const { t } = useI18n()
  const slice = pageSlice(form, page)
  const off = rootIds.every((id) => slice.disabledSections.includes(id))
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!off}
      aria-label={t('cwa.sectionVisible')}
      className={`cwa-switch${off ? '' : ' on'}`}
      onClick={() => onChange(withPageSlice(form, page, {
        ...slice,
        disabledSections: off
          ? slice.disabledSections.filter((id) => !rootIds.includes(id))
          : [...slice.disabledSections, ...rootIds],
      }))}
    >
      <span />
    </button>
  )
}
